import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistApi } from "./AppAssistApi";
import { AppPins } from "./AppPins";

// AppPinFrame - "Remove" on a kept table or chart in the contact page's Insights drawer ("Kept from AI",
// `PinHtml`). The page is SCRIPT-FREE, so - like `AppTaskFrame` and `AppReactions` - this attaches from OUTSIDE
// the `blob:` frame: a small button on every `[data-pin-id]` item under `#wac-pins`, and one click listener.
//
// ⭐ WHY HERE AND NOT IN THE AI PANEL (the design choice, 2026-09-27): the owner looks at kept items in Insights,
// and a pin whose source answer has since left the 200-item AI history could never be reached from the panel.
// The drawer is the one place every pin is shown, so that is where it is removed.
// ⛔ THE REQUEST NAMES THE PIN BY ID ONLY (`AppAssistApi.UnpinBody`, `/assist` `unpin`, owner only). Nothing here
// builds or parses markup: our words go in by `textContent`, a pin id is compared in a loop, never put in a selector.
// ⚠ OPTIMISTIC: the item fades at once ("Removing…"), goes when the service confirms, and comes back with the
// service's own sentence (ink, law 1) when refused. A removed pin stays hidden for the VISIT (`Removed`) - the page
// itself changes at the next rebuild, and until then a reload would otherwise show it again.
// ⛔ NOTHING HERE SENDS A MESSAGE.
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a small fake document.

export interface IPfNode {
    className: string;
    textContent: string | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    appendChild(Child: IPfNode): unknown;
    closest(Selector: string): IPfNode | null;
    querySelector(Selector: string): IPfNode | null;
}

export interface IPfDoc {
    createElement(Tag: string): IPfNode;
    querySelectorAll(Selector: string): ArrayLike<IPfNode>;
    addEventListener(Type: string, Listener: (Ev: Event) => void): void;
}

export interface IPfFrame {
    contentDocument: IPfDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

/** What the overlay needs from the page: the POST to `/assist`, and who is asking. */
export interface IPfHost {
    Post(Body: string, Done: (Status: number, Text: string) => void): void;
    Phone(): string;
    Key(): string;
}

/** One removal in flight - answered once, onto its own item. */
export class AppPinRemoveCall {
    private Frame: AppPinFrame;
    private Item: IPfNode;
    private Id: string;
    private Ended: boolean = false;

    public constructor(Frame: AppPinFrame, Item: IPfNode, Id: string) {
        this.Frame = Frame;
        this.Item = Item;
        this.Id = Id;
    }

    public OnDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Frame.Settled(this.Item, this.Id, AppAssistApi.PinWhy(Status, Text));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinRemoveCall.OnDone");
        }
    };
}

export class AppPinFrame {
    public static readonly PinAttr: string = "data-pin-id";
    public static readonly ActAttr: string = "data-wac-pfact";
    /** page|pin -> removed on this visit. */
    public static Removed: Record<string, boolean> = {};

    private Host: IPfHost;
    private Page: string;
    private Frame: IPfFrame | null = null;
    private Doc: IPfDoc | null = null;
    private Busy: Record<string, boolean> = {};

    public constructor(Host: IPfHost, Page: string) {
        this.Host = Host;
        this.Page = nz(Page);
    }

    public Attach = (Frame: IPfFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.Attach");
        }
    };

    /** The page is in: a Remove button on every kept item (and removed ones hidden) - once per page. */
    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null || this.Frame.contentDocument === null || this.Doc !== null) { return; }
            const Doc: IPfDoc = this.Frame.contentDocument as IPfDoc;
            this.Doc = Doc;
            const Items: ArrayLike<IPfNode> = Doc.querySelectorAll("#wac-pins [" + AppPinFrame.PinAttr + "]");
            if (Items.length === 0) { return; }
            for (let At: number = 0; At < Items.length; At = At + 1) { this.Decorate(Items[At]); }
            Doc.addEventListener("click", this.OnClick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.OnLoad");
        }
    };

    private Decorate = (Item: IPfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Id: string = nz(Item.getAttribute(AppPinFrame.PinAttr));
            if (AppPins.IdPattern.test(Id) !== true || this.Doc === null) { return; }
            if (AppPinFrame.Removed[this.Page + "|" + Id] === true) { Item.className = Item.className + " wac-pfgone"; return; }
            const Button: IPfNode = (this.Doc as IPfDoc).createElement("button");
            Button.setAttribute("type", "button");
            Button.className = "wac-pfrm";
            Button.setAttribute(AppPinFrame.ActAttr, "remove");
            Button.setAttribute("aria-label", AppPinFrame.RemoveAria());
            Button.setAttribute("title", AppPinFrame.RemoveAria());
            Button.textContent = AppPinFrame.RemoveLabel();
            Item.appendChild(Button);
            const Note: IPfNode = (this.Doc as IPfDoc).createElement("span");
            Note.className = "wac-pfnote";
            Note.setAttribute("role", "status");
            Item.appendChild(Note);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.Decorate");
        }
    };

    /** A click on a Remove button: fade the item, ask the service. ⛔ Nothing else in the page is touched. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: IPfNode | null = AppPinFrame.ButtonOf(Ev);
            if (Target === null) { return; }
            const Item: IPfNode | null = Target.closest("[" + AppPinFrame.PinAttr + "]");
            if (Item === null) { return; }
            const Id: string = nz(Item.getAttribute(AppPinFrame.PinAttr));
            if (AppPins.IdPattern.test(Id) !== true || this.Busy[Id] === true) { return; }
            const Body: string = AppAssistApi.UnpinBody(this.Host.Phone(), this.Host.Key(), this.Page, Id);
            if (Body.length === 0) { AppPinFrame.Say(Item, AppPinFrame.SignInNote()); return; }
            this.Busy[Id] = true;
            Item.className = Item.className + " wac-pfbusy";
            AppPinFrame.Say(Item, AppPinFrame.RemovingNote());
            this.Host.Post(Body, new AppPinRemoveCall(this, Item, Id).OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.OnClick");
        }
    };

    /** The service's answer: gone ("" = removed), or back with its sentence. */
    public Settled = (Item: IPfNode, Id: string, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            delete this.Busy[Id];
            Item.className = nz(Item.className).replace(/\s*wac-pfbusy/g, "");
            if (Why.length > 0) { AppPinFrame.Say(Item, Why); return; }
            AppPinFrame.Removed[this.Page + "|" + Id] = true;
            Item.className = Item.className + " wac-pfgone";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.Settled");
        }
    };

    /** The Remove button the event came from, or null. */
    private static ButtonOf = (Ev: Event): IPfNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Target: unknown = Ev.target;
            if (Target === null || typeof Target !== "object") { return null; }
            if (typeof (Target as Record<string, unknown>).closest !== "function") { return null; }
            return (Target as IPfNode).closest("[" + AppPinFrame.ActAttr + "]");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.ButtonOf");
            return null;
        }
    };

    /** The item's own note line, as text. */
    private static Say = (Item: IPfNode, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Note: IPfNode | null = Item.querySelector(".wac-pfnote");
            if (Note !== null) { Note.textContent = nz(Text); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPinFrame.Say");
        }
    };

    /** The overlay's style, carried in the frame's sheet (`AppMain.Frame`). DOCUMENTED EXCEPTION: constant. */
    public static Css = (): string => {
        return ".pin{position:relative}"
            + ".wac-pfrm{font:inherit;font-size:12px;font-weight:600;color:var(--dim);background:none;border:0;"
            + "padding:4px 2px;min-height:28px;cursor:pointer;text-decoration:underline;text-underline-offset:3px}"
            + ".wac-pfrm:hover{color:var(--ink)}.wac-pfnote{display:block;font-size:12px;color:var(--ink)}"
            + ".wac-pfnote:empty{display:none}.wac-pfbusy{opacity:.55}.wac-pfgone{display:none}";
    };

    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static RemoveLabel = (): string => { return "Remove"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static RemoveAria = (): string => { return "Remove this from Kept from AI"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static RemovingNote = (): string => { return "Removing…"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static SignInNote = (): string => { return "Sign in on this browser to remove it."; };
}
